// src/components/StreamActivityMonitor.jsx
//
// Live "pulse" strip: rolling 60s window over the incoming traffic rows.
// Collapsible header (always visible) + expanded body with a sparkline,
// top request types, and per-device/step breakdown. Self-ticking (1s),
// pure client-side — no external chart lib, no MUI dependency (this file
// mounts into MainApp.js, which doesn't use MUI).

import React, { useEffect, useMemo, useState, useCallback } from 'react';

const WINDOW_MS = 60_000;
const BUCKET_MS = 1000;
const BUCKET_COUNT = WINDOW_MS / BUCKET_MS; // 60
const PULSE_MS = 3000;
const COLLAPSE_KEY = 'adlensfx_activity_collapsed';

// Parse a row's time — live rows carry a numeric Date.now(), history-restored
// rows carry an ISO string (see CLAUDE.md: proxy sends ISO, MainApp overrides
// live events with Date.now()). Mirrors the ts() pattern used elsewhere.
function ts(row) {
    // Prefer `timestamp` (raw proxy entries); fall back to `startedAt` for
    // toNetworkRow-transformed rows, which rename timestamp → startedAt.
    const t = row?.timestamp ?? row?.startedAt;
    if (typeof t === 'number') return t;
    if (typeof t === 'string') {
        const parsed = Date.parse(t);
        if (!Number.isNaN(parsed)) return parsed;
    }
    return NaN;
}

function readCollapsed() {
    try {
        return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
        return false;
    }
}

function writeCollapsed(val) {
    try {
        localStorage.setItem(COLLAPSE_KEY, val ? '1' : '0');
    } catch {
        // ignore (private mode / quota)
    }
}

export default function StreamActivityMonitor({ rows, onTypeClick, activeType = null }) {
    const [now, setNow] = useState(() => Date.now());
    const [collapsed, setCollapsed] = useState(readCollapsed);

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(id);
    }, []);

    const toggleCollapsed = useCallback(() => {
        setCollapsed(prev => {
            const next = !prev;
            writeCollapsed(next);
            return next;
        });
    }, []);

    // Header-only numbers: total in window + req/s (last 5s) + "recent" pulse flag.
    // Computed even while collapsed (cheap — single pass, no bucket/type/device work).
    const headerStats = useMemo(() => {
        const list = Array.isArray(rows) ? rows : [];
        let total = 0;
        let last5s = 0;
        let last3s = 0;
        for (let i = 0; i < list.length; i++) {
            const t = ts(list[i]);
            if (Number.isNaN(t)) continue;
            const age = now - t;
            if (age < 0 || age > WINDOW_MS) continue;
            total++;
            if (age <= 5000) last5s++;
            if (age <= PULSE_MS) last3s++;
        }
        return {
            total,
            reqPerSec: last5s / 5,
            pulsing: last3s >= 1,
        };
    }, [rows, now]);

    // Full window breakdown: buckets (sparkline), top types, devices, active step.
    // Skipped entirely while collapsed — not needed for the header.
    const windowData = useMemo(() => {
        if (collapsed) return null;
        const list = Array.isArray(rows) ? rows : [];
        const buckets = new Array(BUCKET_COUNT).fill(0);
        const typeCounts = new Map();
        const deviceCounts = new Map();
        let mostRecentStepRow = null;
        let mostRecentStepTs = -Infinity;

        for (let i = 0; i < list.length; i++) {
            const row = list[i];
            const t = ts(row);
            if (Number.isNaN(t)) continue;
            const age = now - t;
            if (age < 0 || age > WINDOW_MS) continue;

            const bucketIdx = BUCKET_COUNT - 1 - Math.min(BUCKET_COUNT - 1, Math.floor(age / BUCKET_MS));
            if (bucketIdx >= 0 && bucketIdx < BUCKET_COUNT) buckets[bucketIdx]++;

            const type = row?.requestType || 'unknown';
            typeCounts.set(type, (typeCounts.get(type) || 0) + 1);

            const deviceId = row?.deviceId || 'unknown device';
            deviceCounts.set(deviceId, (deviceCounts.get(deviceId) || 0) + 1);

            if (row?.step && t > mostRecentStepTs) {
                mostRecentStepTs = t;
                mostRecentStepRow = row;
            }
        }

        const topTypes = [...typeCounts.entries()].sort((a, b) => b[1] - a[1]);
        const topDevices = [...deviceCounts.entries()].sort((a, b) => b[1] - a[1]);

        return {
            buckets,
            maxBucket: Math.max(1, ...buckets),
            topTypes: topTypes.slice(0, 6),
            otherTypesCount: Math.max(0, topTypes.length - 6),
            topDevices: topDevices.slice(0, 4),
            activeStep: mostRecentStepRow?.step || null,
        };
    }, [rows, now, collapsed]);

    const dotStyle = {
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: headerStats.pulsing ? 'var(--primary)' : 'var(--muted)',
        opacity: headerStats.pulsing ? 1 : 0.4,
        boxShadow: headerStats.pulsing ? '0 0 0 3px var(--primary-muted)' : 'none',
        transition: 'box-shadow 200ms ease, opacity 200ms ease, background 200ms ease',
        flexShrink: 0,
    };

    return (
        <div
            style={{
                border: '1px solid var(--line)',
                borderRadius: 'var(--radius)',
                background: 'var(--card)',
                marginBottom: 8,
                fontSize: 12,
                color: 'var(--ink)',
                overflow: 'hidden',
            }}
        >
            {/* Header — always visible, ~40px */}
            <div
                onClick={toggleCollapsed}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') toggleCollapsed(); }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 12px',
                    cursor: 'pointer',
                    userSelect: 'none',
                }}
                title={collapsed ? 'Expand activity monitor' : 'Collapse activity monitor'}
            >
                <span style={dotStyle} />
                <span style={{ fontWeight: 600, color: 'var(--ink)' }}>Activity</span>
                <span style={{ color: 'var(--muted)' }}>
                    {headerStats.reqPerSec.toFixed(1)} req/s
                </span>
                <span style={{ color: 'var(--muted)' }}>
                    {headerStats.total} in 60s
                </span>
                <span style={{ marginLeft: 'auto', color: 'var(--muted)', fontSize: 14, lineHeight: 1 }}>
                    {collapsed ? '▸' : '▾'}
                </span>
            </div>

            {/* Expanded body */}
            {!collapsed && windowData && (
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 16,
                        padding: '0 12px 10px',
                        borderTop: '1px solid var(--line)',
                        paddingTop: 10,
                    }}
                >
                    {headerStats.total === 0 ? (
                        <div style={{ color: 'var(--muted)', padding: '2px 0', lineHeight: 1.5 }}>
                            💤 No traffic in the last 60s.
                            {rows.length > 0 && ` ${rows.length} request${rows.length === 1 ? '' : 's'} captured on this tab — history is preserved below.`}
                        </div>
                    ) : (
                      <>
                    {/* Sparkline */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        <div style={{ color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            Last 60s
                        </div>
                        <Sparkline buckets={windowData.buckets} max={windowData.maxBucket} />
                    </div>

                    {/* Top request types */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 140 }}>
                        <div style={{ color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            Top types
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 260 }}>
                            {windowData.topTypes.length === 0 && (
                                <span style={{ color: 'var(--muted)' }}>&mdash;</span>
                            )}
                            {windowData.topTypes.map(([type, count]) => {
                                const clickable = typeof onTypeClick === 'function';
                                const isActive = activeType === type;
                                return (
                                    <span
                                        key={type}
                                        className="chip"
                                        onClick={clickable ? (e) => { e.stopPropagation(); onTypeClick(isActive ? null : type); } : undefined}
                                        title={clickable ? (isActive ? 'Clear filter' : `Filter requests to "${type}"`) : undefined}
                                        style={{
                                            cursor: clickable ? 'pointer' : 'default',
                                            padding: '3px 8px',
                                            ...(isActive ? { background: 'var(--accent, #1976d2)', color: '#fff', fontWeight: 600 } : null),
                                        }}
                                    >
                                        {type} &times; {count}
                                    </span>
                                );
                            })}
                            {windowData.otherTypesCount > 0 && (
                                <span style={{ color: 'var(--muted)', padding: '3px 4px' }}>
                                    +{windowData.otherTypesCount} more
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Devices & step */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 140 }}>
                        <div style={{ color: 'var(--muted)', fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                            Devices
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, maxWidth: 260 }}>
                            {windowData.topDevices.length === 0 && (
                                <span style={{ color: 'var(--muted)' }}>&mdash;</span>
                            )}
                            {windowData.topDevices.map(([deviceId, count]) => (
                                <span key={deviceId} className="chip" style={{ cursor: 'default', padding: '3px 8px' }}>
                                    {deviceId} &times; {count}
                                </span>
                            ))}
                            {windowData.activeStep && (
                                <span
                                    className="chip"
                                    style={{
                                        cursor: 'default',
                                        padding: '3px 8px',
                                        color: 'var(--primary)',
                                        borderColor: 'var(--primary-muted)',
                                        background: 'var(--primary-muted)',
                                    }}
                                >
                                    step: {windowData.activeStep}
                                </span>
                            )}
                        </div>
                    </div>
                    </>
                    )}
                </div>
            )}
        </div>
    );
}

function Sparkline({ buckets, max }) {
    const width = 180;
    const height = 36;
    const n = buckets.length;
    const barWidth = width / n;

    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
            {buckets.map((count, i) => {
                const h = count <= 0 ? 0 : Math.max(1, (count / max) * height);
                const x = i * barWidth;
                const y = height - h;
                return (
                    <rect
                        key={i}
                        x={x}
                        y={y}
                        width={Math.max(1, barWidth - 1)}
                        height={h}
                        fill="var(--primary)"
                        opacity={count > 0 ? 0.85 : 0.15}
                    />
                );
            })}
        </svg>
    );
}
